import { takeLatest, call, put, select } from "redux-saga/effects";
import { orderByFilterTable } from "utils/functions";
import YmsNotification from "utils/functions/notification";
import { translate } from "utils/intl/IntlProvider";
import { getError } from "utils/requests/api.errors";
import * as Api from "./document-api";
import { Types, Creators } from "./document-duck";

export const getFilterFromState = (state) => state.Document;
export const getFilterFromStateUserSession = (state) => state.UserSession;

export const onExtractRegisterDataDocuments = (registersData) =>
  registersData.items.map((item) => {
    return {
      id: item.id,
      description: item.description,
    };
  });

export const onExtractRegisterDataEditDocument = (resp) => {
  return {
    documentId: resp.id,
    documentDescription: resp.description,
    maskRules: {
      maskAllowLetters: resp.maskRules.allowLetters,
      maskAllowNumbers: resp.maskRules.allowNumbers,
      maskAllowSpecialCharacters: resp.maskRules.allowSpecialCharacters,
      maskMinCharacters: resp.maskRules.minCharacters,
      maskMaxCharacters: resp.maskRules.maxCharacters,
    },
  };
};

export function* onDocumentFetchAllActivesAsync() {
  const userSessionState = yield select(getFilterFromStateUserSession);
  if (!userSessionState.isAdmin) {
    return;
  }
  try {
    yield put(Creators.documentShowLoadingTable());
    const documentState = yield select(getFilterFromState);
    const isAscending = documentState.filter.ascend === "ascend" ? true : false;
    const orderByFilter = orderByFilterTable(documentState);
    const resp = yield call(Api.getDocuments, {
      filter: documentState.searchFilter,
      _size: documentState.filter.itemsByPage,
      _page: documentState.filter.pageIndex,
      orderBy: orderByFilter,
      ascending: isAscending,
    });
    const registersData = resp.data;
    const registerList = onExtractRegisterDataDocuments(registersData);
    yield put(Creators.documentRegistersActive(registerList, registersData.totalItems));
  } catch (error) {
    const requestErrorMessage = yield getError(error) ?? translate("documents_could_not_be_loaded");
    yield YmsNotification({
      type: "error",
      description: requestErrorMessage,
    });
  } finally {
    yield put(Creators.documentHideLoadingTable());
  }
}

export function* onDocumentSaveNewDocumentAsync(action: any) {
  const userSessionState = yield select(getFilterFromStateUserSession);
  if (!userSessionState.isAdmin) {
    return;
  }
  try {
    yield put(Creators.documentShowLoading());
    yield put(Creators.documentDisabledRegisterButton());
    yield call(Api.saveNewDocument, action.body);
    yield put(Creators.documentEnabledRegisterButton());
    yield put(Creators.documentHideDrawerNewDocument());
    const registrationSuccessfullyMessage = yield translate("registration_successfully!");
    yield YmsNotification({
      type: "success",
      description: registrationSuccessfullyMessage,
    });
    yield onDocumentFetchAllActivesAsync();
  } catch (error) {
    console.error(error);
    yield put(Creators.documentEnabledRegisterButton());
    const registrationErrorMessage = yield getError(error) ?? translate("cannot_register");
    yield YmsNotification({
      type: "error",
      description: registrationErrorMessage,
    });
  } finally {
    yield put(Creators.documentHideLoading());
  }
}

export function* onDocumentDeleteByIdAsync(action: any) {
  const userSessionState = yield select(getFilterFromStateUserSession);
  if (!userSessionState.isAdmin) {
    return;
  }
  try {
    yield put(Creators.documentShowLoading());
    yield call(Api.deleteDocument, action.body.id);
    const registrationSuccessfullyMessage = yield translate("document_removed_with_successfully");
    yield YmsNotification({
      type: "success",
      description: registrationSuccessfullyMessage,
    });
    yield onDocumentFetchAllActivesAsync();
  } catch (error) {
    console.error(error);
    const requestErrorMessage = yield getError(error) ?? translate("could_not_remove_document");
    yield YmsNotification({
      type: "error",
      description: requestErrorMessage,
    });
  } finally {
    yield put(Creators.documentHideLoading());
  }
}

export function* onDocumentUpdateDocumentAsync(action: any) {
  const userSessionState = yield select(getFilterFromStateUserSession);
  if (!userSessionState.isAdmin) {
    return;
  }
  try {
    const requestBody = {
      description: action.body.description,
      maskRules: {
        allowLetters: action.body.maskAllowLetters,
        allowNumbers: action.body.maskAllowNumbers,
        allowSpecialCharacters: action.body.maskAllowSpecialCharacters,
        minCharacters: action.body.maskMinCharacters,
        maxCharacters: action.body.maskMaxCharacters,
      },
    };
    yield put(Creators.documentShowLoading());
    yield call(Api.updateDocument, requestBody, action.body.documentId);
    yield put(Creators.documentHideDrawerEditDocument());
    const registrationSuccessfullyMessage = yield translate("update_successfully");
    yield YmsNotification({
      type: "success",
      description: registrationSuccessfullyMessage,
    });
    yield onDocumentFetchAllActivesAsync();
  } catch (error) {
    const registrationErrorMessage = yield translate("cannot_register");
    yield YmsNotification({
      type: "error",
      description: registrationErrorMessage,
    });
  } finally {
    yield put(Creators.documentHideLoading());
  }
}

export function* onDocumentFetchByIdAsync(action) {
  const userSessionState = yield select(getFilterFromStateUserSession);
  if (!userSessionState.isAdmin) {
    return;
  }
  try {
    yield put(Creators.documentShowLoading());
    const resp = yield call(Api.getDocumentById, { id: action.id });
    const data = onExtractRegisterDataEditDocument(resp.data);
    yield put(Creators.documentUpdateDocument(data));
  } catch (error) {
    console.error(error);
    const requestErrorMessage = yield getError(error) ?? translate("document_could_not_be_loaded");
    yield YmsNotification({
      type: "error",
      description: requestErrorMessage,
    });
  } finally {
    yield put(Creators.documentHideLoading());
  }
}

export default function* DocumentSagas() {
  yield takeLatest(Types.DOCUMENT_FETCH_ALL_ACTIVES_ASYNC, onDocumentFetchAllActivesAsync);
  yield takeLatest(Types.DOCUMENT_SAVE_NEW_DOCUMENT_ASYNC, onDocumentSaveNewDocumentAsync);
  yield takeLatest(Types.DOCUMENT_DELETE_BY_ID_ASYNC, onDocumentDeleteByIdAsync);
  yield takeLatest(Types.DOCUMENT_UPDATE_DOCUMENT_ASYNC, onDocumentUpdateDocumentAsync);
  yield takeLatest(Types.DOCUMENT_FETCH_BY_ID_ASYNC, onDocumentFetchByIdAsync);
}
